import { useMutation, useQuery } from '@apollo/client'
import { AddCircle, Delete, Edit } from '@mui/icons-material'
import { Button, Grid, IconButton, Typography } from '@mui/material'
import { ReferencesOptionsQuery } from '__generated__/graphql'
import ToggleEditAutocomplete from 'candidate/components/ToggleEditFormFields/ToggleEditAutocomplete'
import ToggleEditCompaniesAutocomplete from 'candidate/components/ToggleEditFormFields/ToggleEditCompaniesAutocomplete'
import ToggleEditTextField from 'candidate/components/ToggleEditFormFields/ToggleEditTextField'
import {
    EditButtonContainer,
    StyledForm,
    SubtitleContainer,
    TitleContainer,
} from 'candidate/components/styles'
import {
    CandidateReferencesOptionsQuery,
    updateCandidateReferencesMutation,
} from 'candidate/graphql/profile.gql'
import isEqual from 'lodash.isequal'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { isValidPhoneNumber } from 'react-phone-number-input'
import useToast from 'shared/components/Toast/useToast'
import useIsMobile from 'shared/hooks/useIsMobile'
import { getPxAsRem } from 'shared/utils'
import * as Yup from 'yup'
import { FormComponentProps, PROFILE_SECTIONS } from './types'

const References: FC<FormComponentProps> = ({
    data,
    loading,
    refetch,
    edit,
    setEdit,
    setUnsavedChangesExist,
    closeEditWithoutModal,
}) => {
    const toast = useToast()
    const isMobile = useIsMobile()
    const initialReferences = useMemo(
        () => data.candidate?.references ?? [],
        [data.candidate]
    )
    const [mutate] = useMutation(updateCandidateReferencesMutation)
    const { data: referenceOptions } = useQuery<ReferencesOptionsQuery>(
        CandidateReferencesOptionsQuery
    )

    const titleOptions = referenceOptions?.titles
    const [referencesFormValues, setReferencesFormValues] =
        useState(initialReferences)
    const defaultErrorValues = useMemo(
        () =>
            initialReferences?.map(() => ({
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                workTitleId: '',
                company: '',
            })) ?? [],
        [initialReferences]
    )
    const [errorValues, setErrorValues] = useState(defaultErrorValues)

    const handleEditClick = () => {
        if (edit) {
            setEdit(null)
        } else {
            setEdit(PROFILE_SECTIONS.REFERENCES)
        }
    }

    const resetForm = useCallback(() => {
        setReferencesFormValues(initialReferences)
        setErrorValues(defaultErrorValues)
    }, [defaultErrorValues, initialReferences])

    const handleChange = (newValue, index, field) => {
        setReferencesFormValues((prev) => {
            const newReferences = [...prev]
            newReferences[index] = {
                ...newReferences[index],
                [field]: newValue,
            }
            return newReferences
        })
    }

    const handleErrorChange = (newValue, index, field) => {
        setErrorValues((prev) => {
            const newReferences = [...prev]
            newReferences[index] = {
                ...newReferences[index],
                [field]: newValue,
            }
            return newReferences
        })
    }

    const handleAddReference = () => {
        if (!edit) setEdit(PROFILE_SECTIONS.REFERENCES)
        setReferencesFormValues((prev) => [
            ...prev,
            {
                id: '',
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                workTitleId: '',
                company: null,
            },
        ])
        setErrorValues((prev) => [
            ...prev,
            {
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                workTitleId: '',
                company: '',
            },
        ])
    }

    const handleRemove = (index) => {
        if (!edit) setEdit(PROFILE_SECTIONS.REFERENCES)
        setReferencesFormValues((prev) => {
            const next = [...prev]
            next.splice(index, 1)
            return next
        })
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            const references =
                referencesFormValues?.map((reference) => ({
                    firstName: reference.firstName,
                    lastName: reference.lastName,
                    email: reference.email,
                    phone: reference.phone,
                    workTitleId: reference.workTitleId,
                    companyId: reference.company?.id,
                })) ?? []

            const response = await mutate({
                variables: {
                    id: data.candidate?.user?.id,
                    references,
                },
            })
            if (response.data.updateCandidateReferences.success) {
                toast({
                    title: 'Success',
                    message: 'References updated successfully',
                    severity: 'success',
                })
                refetch()
                closeEditWithoutModal()
            } else {
                toast({
                    title: 'Error',
                    message: 'Something went wrong. Please try again.',
                    severity: 'error',
                })
            }
        } catch (error) {
            console.log(error)
            toast({
                title: 'Error',
                message: 'Something went wrong. Please try again.',
                severity: 'error',
            })
        }
    }

    const unsavedChangesExist = useMemo(() => {
        if (referencesFormValues?.length !== initialReferences?.length)
            return true
        if (!isEqual(referencesFormValues, initialReferences)) return true
        return false
    }, [referencesFormValues, initialReferences])

    const isDisabled = useMemo(() => {
        if (!unsavedChangesExist) return true
        const hasEmptyValues = referencesFormValues?.reduce(
            (acc, formValue) => {
                if (acc) {
                    return acc
                } else {
                    // id can be null for a new reference and we are ok with that
                    const formValueToCheck = { ...formValue, id: '_' }
                    return Object.values(formValueToCheck).some(
                        (value) => value === ''
                    )
                }
            },
            false
        )
        if (hasEmptyValues) return true
        const hasErrors = errorValues.reduce((acc, errorValue) => {
            if (acc) {
                return acc
            } else {
                return Object.values(errorValue).some((value) => value !== '')
            }
        }, false)
        return hasErrors
    }, [referencesFormValues, errorValues, unsavedChangesExist])

    useEffect(
        () => setUnsavedChangesExist(unsavedChangesExist),
        [unsavedChangesExist, setUnsavedChangesExist]
    )

    useEffect(() => {
        if (!edit) resetForm()
    }, [edit, resetForm])

    return (
        <>
            <TitleContainer>
                <Typography variant="h6" component="h2">
                    References
                </Typography>
                <IconButton onClick={handleEditClick} disabled={loading}>
                    <Edit fontSize="small" />
                </IconButton>
            </TitleContainer>
            <StyledForm onSubmit={handleSubmit}>
                {referencesFormValues?.map(
                    (
                        {
                            firstName,
                            lastName,
                            email,
                            phone,
                            id,
                            workTitleId,
                            company,
                        },
                        index
                    ) => (
                        <Grid
                            key={id || index}
                            container
                            rowSpacing={3}
                            columnSpacing={2}
                            sx={{ marginBottom: getPxAsRem(isMobile ? 8 : 32) }}
                        >
                            <SubtitleContainer item xs={12}>
                                <Typography variant="subtitle2">
                                    Reference {index + 1}
                                </Typography>
                                {(index !== 0 ||
                                    referencesFormValues?.length > 1) && (
                                    <IconButton
                                        onClick={() => handleRemove(index)}
                                        aria-label="remove reference"
                                    >
                                        <Delete />
                                    </IconButton>
                                )}
                            </SubtitleContainer>
                            <Grid item xs={12} md={6}>
                                <ToggleEditTextField
                                    label="First Name"
                                    value={firstName}
                                    edit={edit}
                                    helperText={errorValues[index].firstName}
                                    error={!!errorValues[index].firstName}
                                    onChange={(e) => {
                                        handleChange(
                                            e.target.value,
                                            index,
                                            'firstName'
                                        )
                                        if (!e.target.value) {
                                            handleErrorChange(
                                                'First Name is required',
                                                index,
                                                'firstName'
                                            )
                                        } else {
                                            handleErrorChange(
                                                '',
                                                index,
                                                'firstName'
                                            )
                                        }
                                    }}
                                    name="firstName"
                                    onBlur={(e) => {
                                        if (!e.target.value) {
                                            handleErrorChange(
                                                'First Name is required',
                                                index,
                                                'firstName'
                                            )
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <ToggleEditTextField
                                    label="Last Name"
                                    value={lastName}
                                    edit={edit}
                                    helperText={errorValues[index].lastName}
                                    error={!!errorValues[index].lastName}
                                    onChange={(e) => {
                                        handleChange(
                                            e.target.value,
                                            index,
                                            'lastName'
                                        )
                                        if (!e.target.value) {
                                            handleErrorChange(
                                                'Last Name is required',
                                                index,
                                                'lastName'
                                            )
                                        } else {
                                            handleErrorChange(
                                                '',
                                                index,
                                                'lastName'
                                            )
                                        }
                                    }}
                                    name="lastName"
                                    onBlur={(e) => {
                                        if (!e.target.value) {
                                            handleErrorChange(
                                                'Last Name is required',
                                                index,
                                                'lastName'
                                            )
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <ToggleEditTextField
                                    label="Email"
                                    helperText={errorValues[index].email}
                                    error={!!errorValues[index].email}
                                    value={email}
                                    edit={edit}
                                    onChange={(e) => {
                                        handleChange(
                                            e.target.value,
                                            index,
                                            'email'
                                        )
                                        if (!e.target.value) {
                                            handleErrorChange(
                                                'Email is required',
                                                index,
                                                'email'
                                            )
                                        } else {
                                            handleErrorChange(
                                                '',
                                                index,
                                                'email'
                                            )
                                        }
                                    }}
                                    name="email"
                                    onBlur={(e) => {
                                        if (!e.target.value) {
                                            handleErrorChange(
                                                'Email is required',
                                                index,
                                                'email'
                                            )
                                        } else if (
                                            !Yup.string()
                                                .email()
                                                .isValidSync(e.target.value)
                                        ) {
                                            handleErrorChange(
                                                'Email is invalid',
                                                index,
                                                'email'
                                            )
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <ToggleEditTextField
                                    label="Phone"
                                    mask="phone"
                                    helperText={errorValues[index].phone}
                                    error={!!errorValues[index].phone}
                                    value={phone}
                                    edit={edit}
                                    onChange={(newValue) => {
                                        handleChange(newValue, index, 'phone')
                                        if (!newValue) {
                                            handleErrorChange(
                                                'Phone is required',
                                                index,
                                                'phone'
                                            )
                                        } else {
                                            handleErrorChange(
                                                '',
                                                index,
                                                'phone'
                                            )
                                        }
                                    }}
                                    name="phone"
                                    onBlur={() => {
                                        if (!phone) {
                                            handleErrorChange(
                                                'Phone is required',
                                                index,
                                                'phone'
                                            )
                                        } else if (!isValidPhoneNumber(phone)) {
                                            handleErrorChange(
                                                'Phone is invalid',
                                                index,
                                                'phone'
                                            )
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <ToggleEditAutocomplete
                                    labelId="workTitle"
                                    label="Work Title"
                                    value={titleOptions?.find(
                                        (title) => title.id === workTitleId
                                    )}
                                    options={titleOptions || []}
                                    edit={edit}
                                    onChange={(event, newValue) => {
                                        if (!Array.isArray(newValue)) {
                                            handleChange(
                                                newValue?.id,
                                                index,
                                                'workTitleId'
                                            )
                                            if (!newValue) {
                                                handleErrorChange(
                                                    'Phone is required',
                                                    index,
                                                    'phone'
                                                )
                                            } else {
                                                handleErrorChange(
                                                    '',
                                                    index,
                                                    'phone'
                                                )
                                            }
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <ToggleEditCompaniesAutocomplete
                                    labelId="company"
                                    label="Company"
                                    value={company}
                                    size="medium"
                                    edit={edit}
                                    onChange={(event, newValue) => {
                                        handleChange(newValue, index, 'company')
                                    }}
                                />
                            </Grid>
                            {index === referencesFormValues.length - 1 && (
                                <EditButtonContainer item xs={12}>
                                    <Button
                                        onClick={handleAddReference}
                                        variant="text"
                                        startIcon={<AddCircle />}
                                    >
                                        Add Reference
                                    </Button>
                                </EditButtonContainer>
                            )}
                        </Grid>
                    )
                )}
                {!referencesFormValues?.length && (
                    <EditButtonContainer item xs={12}>
                        <Button
                            onClick={handleAddReference}
                            variant="text"
                            startIcon={<AddCircle />}
                        >
                            Add Reference
                        </Button>
                    </EditButtonContainer>
                )}
                {edit && (
                    <EditButtonContainer item xs={12}>
                        <Button
                            variant="outlined"
                            type="reset"
                            onClick={() => {
                                closeEditWithoutModal()
                                resetForm()
                            }}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isDisabled}>
                            Save and Update
                        </Button>
                    </EditButtonContainer>
                )}
            </StyledForm>
        </>
    )
}

export default References
